import { Card, CardContent } from '@/components/ui/card';
import { FileText, Trash2 } from 'lucide-react';
import { fmtCurrency, fmtDate } from '@/lib/receiptUtils';

export function RecentList({ receipts, onOpen, onDelete }) {
  if (!receipts?.length) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-[#5b6773]">
          No processed receipts yet.
        </CardContent>
      </Card>
    );
  }
  // Two buckets: committed receipts ("Saved") and anything still mid-flow
  // ("In progress"). Sorted descending by uploaded_at within each bucket
  // (the API already returns them that way).
  const saved = receipts.filter(r => r.status === 'committed');
  const inProgress = receipts.filter(r => r.status !== 'committed');

  const renderRow = (r) => {
    const snapProjects = r.committed_snapshot?.projects || {};
    const projectCount = Object.keys(snapProjects).length;
    const itemCount = Object.values(snapProjects).reduce(
      (s, p) => s + (p.item_contributions?.length || 0), 0,
    );
    const committedTotal = Object.values(snapProjects).reduce(
      (s, p) => s + (p.item_contributions || []).reduce((x, c) => x + (c.amount || 0), 0), 0,
    );
    return (
      <button
        key={r.id}
        onClick={() => onOpen(r)}
        className="group w-full text-left px-4 py-3 hover:bg-[#2d948c]/[0.05] transition-colors flex items-start gap-3"
        data-testid={`recent-receipt-${r.id}`}
      >
        <FileText className="h-4 w-4 text-[#5b6773] mt-0.5 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm text-[#1f2933] truncate">{r.vendor || r.file_name || 'Receipt'}</p>
            {r.status === 'committed' && (
              <span className="text-[9px] uppercase tracking-wider font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 rounded-full">
                committed
              </span>
            )}
          </div>
          <p className="text-xs text-[#5b6773] mt-0.5">
            {(r.items?.length || 0)} line item{(r.items?.length || 0) === 1 ? '' : 's'}
            {r.total_detected != null ? ` · ${fmtCurrency(r.total_detected)} printed` : ''}
          </p>
          {projectCount > 0 && (
            <p className="text-[10px] text-[#2d948c] mt-0.5 font-medium">
              {fmtCurrency(committedTotal)} → {itemCount} item{itemCount === 1 ? '' : 's'} in {projectCount} project{projectCount === 1 ? '' : 's'}
            </p>
          )}
          <p className="text-[10px] text-[#5b6773]/70 mt-0.5">{fmtDate(r.uploaded_at)}</p>
        </div>
        <Trash2
          className="h-3.5 w-3.5 text-[#5b6773]/40 opacity-0 group-hover:opacity-100 transition-opacity hover:text-destructive"
          onClick={(e) => { e.stopPropagation(); onDelete(r.id); }}
        />
      </button>
    );
  };

  return (
    <div className="space-y-3">
      {inProgress.length > 0 && (
        <Card data-testid="receipts-in-progress">
          <CardContent className="p-0">
            <p className="px-4 py-3 text-[10px] uppercase tracking-wider text-[#5b6773] font-semibold border-b">
              In progress ({inProgress.length})
            </p>
            <div className="divide-y">{inProgress.map(renderRow)}</div>
          </CardContent>
        </Card>
      )}
      {saved.length > 0 && (
        <Card data-testid="receipts-saved">
          <CardContent className="p-0">
            <p className="px-4 py-3 text-[10px] uppercase tracking-wider text-emerald-700 font-semibold border-b bg-emerald-50/40">
              Saved receipts ({saved.length})
            </p>
            <div className="divide-y">{saved.map(renderRow)}</div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
